/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from "react";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    DialogActions,
    Input,
    Tooltip,
} from "@fluentui/react-components";
import { Add20Regular, DocumentText20Regular } from "@fluentui/react-icons";

interface IDescriptionDialogTypes {
    hasDescription: string | null;
    id: string | undefined;
    token: string;
    cellKey: string;
    submitReady: (body: { description: string }, cellKey: string, id: string) => void;
    locked?: boolean;
    /** Optional handler to reset default hours for this entry's day back to auto mode */
    onResetDefaultHours?: () => Promise<void> | void;
}

export default function DescriptionDialog(props: IDescriptionDialogTypes) {
    const [popup, setPopup] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleSubmit = (description: string) => {
        setPopup(false);

        const body = {
            description: description,
        };
        props.submitReady(body, props.cellKey, props.id as string);
    };

    // Handle ESC key to close dialog
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === "Escape" && popup) {
                setPopup(false);
            }
        };

        if (popup) {
            document.addEventListener("keydown", handleEscKey);
        }

        return () => {
            document.removeEventListener("keydown", handleEscKey);
        };
    }, [popup]);

    const tooltipContent = props.hasDescription ? props.hasDescription : "Add description";

    const isLocked = props.locked || false;

    return (
        <Dialog open={popup} onOpenChange={(_, data) => {
            if (!isLocked) {
                setPopup(!!data.open);
            }
        }}>
            <DialogTrigger disableButtonEnhancement>
                <Button
                    className="hover:bg-transparent! active:bg-transparent! min-w-0 w-full h-full p-0"
                    icon={
                        <Tooltip content={isLocked ? "Time entries are locked" : tooltipContent} relationship="label">
                            <span className={`h-full w-full flex items-center justify-center transition-all duration-200 group/icon ${
                                isLocked 
                                    ? "text-gray-400 cursor-not-allowed" 
                                    : "text-(--colorBrandForeground1) hover:text-(--colorBrandForeground1Hover)"
                            }`}>
                                {props.hasDescription ? (
                                    <DocumentText20Regular className="h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 group-hover/icon:scale-110 group-hover/icon:opacity-90" />
                                ) : (
                                    <Add20Regular className="h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 group-hover/icon:scale-110 group-hover/icon:opacity-90" />
                                )}
                            </span>
                        </Tooltip>
                    }
                    onClick={() => {
                        if (!isLocked) {
                            setPopup(true);
                        }
                    }}
                    appearance="subtle"
                    size="small"
                    disabled={isLocked}
                />
            </DialogTrigger>
            <DialogSurface className="min-w-[85%]">
                <DialogBody>
                    <DialogTitle>Add description</DialogTitle>
                    <DialogContent>
                        <Input
                            className="w-full h-10"
                            autoComplete="off"
                            maxLength={100}
                            defaultValue={props.hasDescription as string | undefined}
                            ref={inputRef}
                            disabled={isLocked}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !isLocked) {
                                    if (inputRef !== null && inputRef.current !== null) {
                                        handleSubmit(inputRef.current.value);
                                    }
                                }
                            }}
                        />
                    </DialogContent>
                    {/* Dialog Actions */}
                    <DialogActions>
                        {/* Reset to auto default hours (when applicable) */}
                        {props.onResetDefaultHours && (
                            <Button
                                appearance="secondary"
                                disabled={isLocked}
                                onClick={async () => {
                                    if (isLocked) return;
                                    await props.onResetDefaultHours?.();
                                    setPopup(false);
                                }}
                            >
                                Reset default hours
                            </Button>
                        )}
                        {/* Cancel Button */}
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary" onClick={() => setPopup(false)}>
                                Cancel
                            </Button>
                        </DialogTrigger>
                        {/* Submit Button */}
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance="primary"
                                disabled={isLocked}
                                onClick={() => {
                                    if (!isLocked && inputRef !== null && inputRef.current !== null) {
                                        handleSubmit(inputRef.current.value);
                                    }
                                }}
                            >
                                Submit
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}
